import clsx from "clsx";
import React, { useEffect, useRef } from "react";

export function DualScrollbarContainer({
  children,
  className,
  fillHeight,
}: {
  children: React.ReactNode;
  className?: string;
  /**
   * Flex into the parent (which must be a flex column) and scroll the
   * content vertically inside.
   */
  fillHeight?: boolean;
}) {
  const topScrollerRef = useRef<HTMLDivElement>(null);
  const bottomScrollerRef = useRef<HTMLDivElement>(null);
  const scrollSpacerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const top = topScrollerRef.current;
    const bottom = bottomScrollerRef.current;
    const spacer = scrollSpacerRef.current;

    if (!top || !bottom || !spacer) return;

    const updateSpacerWidth = () => {
      spacer.style.width = `${bottom.scrollWidth}px`;
    };

    updateSpacerWidth();

    let resizeAnimationFrame: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeAnimationFrame !== null) {
        cancelAnimationFrame(resizeAnimationFrame);
      }
      resizeAnimationFrame = requestAnimationFrame(() => {
        updateSpacerWidth();
        resizeAnimationFrame = null;
      });
    });
    resizeObserver.observe(bottom);

    const innerContent = bottom.firstElementChild;
    if (innerContent) {
      resizeObserver.observe(innerContent);
    }

    // The scrollLeft we last assigned to each scroller, so we can recognise the
    // `scroll` event echoing that write back and not bounce it. A flag cleared
    // on an animation frame can't do this: WebKit coalesces scroll events
    // during momentum scrolling, so the echo can land after the flag is
    // cleared, and the bounced write then cancels the fling it lands in.
    const lastWritten = new Map<HTMLDivElement, number>();

    const mirror = (from: HTMLDivElement, to: HTMLDivElement) => () => {
      const position = from.scrollLeft;
      const echo = lastWritten.get(from);
      lastWritten.delete(from); // only the immediate echo counts
      if (echo === position) return;
      // Already in sync (sub-pixel tolerance for fractional scroll offsets).
      if (Math.abs(to.scrollLeft - position) <= 0.5) return;
      to.scrollLeft = position;
      // Read back rather than storing `position`: `to` clamps to its own max
      // scroll, which differs from `from`'s when only one of them carries a
      // vertical scrollbar — and it's the clamped value the echo will carry.
      lastWritten.set(to, to.scrollLeft);
    };

    const topScrollHandler = mirror(top, bottom);
    const bottomScrollHandler = mirror(bottom, top);

    top.addEventListener("scroll", topScrollHandler);
    bottom.addEventListener("scroll", bottomScrollHandler);

    return () => {
      top.removeEventListener("scroll", topScrollHandler);
      bottom.removeEventListener("scroll", bottomScrollHandler);
      resizeObserver.disconnect();
      if (resizeAnimationFrame !== null) {
        cancelAnimationFrame(resizeAnimationFrame);
      }
    };
  }, []);

  return (
    <div
      className={clsx(className, fillHeight && "flex flex-col flex-1 min-h-0")}
    >
      <div
        ref={topScrollerRef}
        className="overflow-x-auto overflow-y-hidden mb-1 shrink-0"
        style={{ height: "17px" }}
      >
        <div ref={scrollSpacerRef} style={{ height: "1px" }} />
      </div>

      {/* `relative` makes this scroller the containing block for absolutely
          positioned descendants (e.g. Radix checkboxes' hidden bubble inputs),
          so they scroll and clip with the content instead of overflowing the
          page through this statically-positioned container. */}
      <div
        ref={bottomScrollerRef}
        className={clsx(
          "relative overflow-x-auto w-full",
          fillHeight && "overflow-y-auto flex-1 min-h-0",
        )}
      >
        {children}
      </div>
    </div>
  );
}
